'use client';

import React from 'react';
import {
  Users,
  ChevronRight,
  Building2,
  AlertCircle,
  Layers,
  Sparkles,
  UserPlus,
  ShieldCheck,
  Globe,
  Zap,
  FileText,
} from 'lucide-react';
import { PlatformStats, KycItem } from '../types';

interface OverviewTabProps {
  stats: PlatformStats | null;
  kycList: KycItem[];
  setActiveTab: (tab: 'OVERVIEW' | 'KYC' | 'WORKSPACES' | 'USERS' | 'AUDIT_LOG') => void;
  setUserFilter: (filter: 'ALL' | 'ORGANIZATION' | 'MEMBER' | 'SUSPENDED') => void;
  setUsersPage: (page: number) => void;
  setWsPage: (page: number) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
  stats,
  kycList,
  setActiveTab,
  setUserFilter,
  setUsersPage,
  setWsPage,
}) => {
  return (
    <div className="space-y-8">
      {/* Interactive Hero Stat Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <button
          onClick={() => {
            setActiveTab('USERS');
            setUserFilter('ALL');
            setUsersPage(1);
          }}
          className="text-left bg-gradient-to-br from-[#0E1324] via-[#0B0F1C] to-[#060811] border border-indigo-500/20 rounded-3xl p-6 shadow-2xl relative overflow-hidden group hover:border-indigo-500/50 hover:scale-[1.02] transition-all cursor-pointer"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-500/10 rounded-full blur-2xl group-hover:bg-indigo-500/25 transition-all"></div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Total System Users</span>
            <div className="w-10 h-10 rounded-2xl bg-indigo-500/15 border border-indigo-500/30 text-indigo-400 flex items-center justify-center group-hover:bg-indigo-500 group-hover:text-white transition-all">
              <Users className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-white mt-4 tracking-tight">{stats?.totalUsers || 0}</p>
          <div className="flex items-center justify-between text-xs text-indigo-400 font-bold mt-4 pt-3 border-t border-slate-800/80">
            <span className="flex items-center gap-1">Open User Directory <ChevronRight className="w-3.5 h-3.5" /></span>
            <span className="text-[10px] text-slate-500 font-normal">Click to View</span>
          </div>
        </button>

        <button
          onClick={() => {
            setActiveTab('USERS');
            setUserFilter('ORGANIZATION');
            setUsersPage(1);
          }}
          className="text-left bg-gradient-to-br from-[#0E1324] via-[#0B0F1C] to-[#060811] border border-purple-500/20 rounded-3xl p-6 shadow-2xl relative overflow-hidden group hover:border-purple-500/50 hover:scale-[1.02] transition-all cursor-pointer"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-purple-500/10 rounded-full blur-2xl group-hover:bg-purple-500/25 transition-all"></div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Organizations</span>
            <div className="w-10 h-10 rounded-2xl bg-purple-500/15 border border-purple-500/30 text-purple-400 flex items-center justify-center group-hover:bg-purple-500 group-hover:text-white transition-all">
              <Building2 className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-white mt-4 tracking-tight">{stats?.totalOrganizations || 0}</p>
          <div className="flex items-center justify-between text-xs text-purple-400 font-bold mt-4 pt-3 border-t border-slate-800/80">
            <span className="flex items-center gap-1">Filter Organizations <ChevronRight className="w-3.5 h-3.5" /></span>
            <span className="text-[10px] text-slate-500 font-normal">Click to View</span>
          </div>
        </button>

        <button
          onClick={() => setActiveTab('KYC')}
          className="text-left bg-gradient-to-br from-[#0E1324] via-[#0B0F1C] to-[#060811] border border-amber-500/20 rounded-3xl p-6 shadow-2xl relative overflow-hidden group hover:border-amber-500/50 hover:scale-[1.02] transition-all cursor-pointer"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/10 rounded-full blur-2xl group-hover:bg-amber-500/25 transition-all"></div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Pending KYC Approvals</span>
            <div className="w-10 h-10 rounded-2xl bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center group-hover:bg-amber-500 group-hover:text-slate-950 transition-all">
              <AlertCircle className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-amber-400 mt-4 tracking-tight">{kycList.length}</p>
          <div className="flex items-center justify-between text-xs text-amber-400 font-bold mt-4 pt-3 border-t border-slate-800/80">
            <span className="flex items-center gap-1">Review Applications <ChevronRight className="w-3.5 h-3.5" /></span>
            <span className="text-[10px] text-slate-500 font-normal">Action Queue</span>
          </div>
        </button>

        <button
          onClick={() => {
            setActiveTab('WORKSPACES');
            setWsPage(1);
          }}
          className="text-left bg-gradient-to-br from-[#0E1324] via-[#0B0F1C] to-[#060811] border border-emerald-500/20 rounded-3xl p-6 shadow-2xl relative overflow-hidden group hover:border-emerald-500/50 hover:scale-[1.02] transition-all cursor-pointer"
        >
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/10 rounded-full blur-2xl group-hover:bg-emerald-500/25 transition-all"></div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-black text-slate-400 uppercase tracking-wider">Workspaces & Boards</span>
            <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 flex items-center justify-center group-hover:bg-emerald-500 group-hover:text-white transition-all">
              <Layers className="w-5 h-5" />
            </div>
          </div>
          <p className="text-3xl font-black text-white mt-4 tracking-tight">{stats?.totalWorkspaces || 0}</p>
          <div className="flex items-center justify-between text-xs text-emerald-400 font-bold mt-4 pt-3 border-t border-slate-800/80">
            <span className="flex items-center gap-1">View All Workspaces <ChevronRight className="w-3.5 h-3.5" /></span>
            <span className="text-[10px] text-slate-500 font-normal">{stats?.totalBoards || 0} Boards</span>
          </div>
        </button>
      </div>

      {/* Activity Stream & Governance Hub */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-sm font-black text-white flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-400" />
              <span>Recent System Activity & Governance Logs</span>
            </h3>
            <span className="text-xs text-slate-500 font-mono">Live Stream</span>
          </div>

          <div className="space-y-4">
            <div className="p-4 bg-[#060811] border border-slate-800/60 rounded-2xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-bold text-xs">
                  <UserPlus className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-white">Superadmin Access Synchronized</p>
                  <p className="text-[10px] text-slate-400">admin@cognoflux.com has owner privilege across all workspaces</p>
                </div>
              </div>
              <span className="text-[10px] font-mono text-emerald-400 font-bold">Active</span>
            </div>

            <div className="p-4 bg-[#060811] border border-slate-800/60 rounded-2xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-purple-500/10 text-purple-400 flex items-center justify-center font-bold text-xs">
                  <ShieldCheck className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-white">Organization KYC Verification</p>
                  <p className="text-[10px] text-slate-400">11 Existing Organizations auto-approved in PostgreSQL database</p>
                </div>
              </div>
              <span className="text-[10px] font-mono text-purple-400 font-bold">Completed</span>
            </div>

            <div className="p-4 bg-[#060811] border border-slate-800/60 rounded-2xl flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-bold text-xs">
                  <Globe className="w-4 h-4" />
                </div>
                <div>
                  <p className="text-xs font-extrabold text-white">Dedicated Subdomain Isolated</p>
                  <p className="text-[10px] text-slate-400">Nginx route board-admin.cognoflux.com pointing to port 3051</p>
                </div>
              </div>
              <span className="text-[10px] font-mono text-emerald-400 font-bold">Online</span>
            </div>
          </div>
        </div>

        <div className="bg-[#0A0D1A] border border-slate-800/80 rounded-3xl p-6 shadow-2xl flex flex-col justify-between">
          <div>
            <h3 className="text-sm font-black text-white flex items-center gap-2 mb-4">
              <Zap className="w-4 h-4 text-amber-400" />
              <span>Quick Governance Actions</span>
            </h3>
            <div className="space-y-3">
              <button
                onClick={() => setActiveTab('KYC')}
                className="w-full p-3.5 bg-[#060811] hover:bg-slate-900 border border-slate-800 rounded-2xl text-xs font-bold text-slate-200 flex items-center justify-between transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <FileText className="w-4 h-4 text-amber-400" /> Review Pending KYCs
                </span>
                <span className="px-2 py-0.5 bg-amber-400 text-slate-950 text-[10px] font-black rounded-full group-hover:scale-105 transition-transform">
                  {kycList.length}
                </span>
              </button>

              <button
                onClick={() => {
                  setActiveTab('WORKSPACES');
                  setWsPage(1);
                }}
                className="w-full p-3.5 bg-[#060811] hover:bg-slate-900 border border-slate-800 rounded-2xl text-xs font-bold text-slate-200 flex items-center justify-between transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-400" /> Inspect All Workspaces
                </span>
                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-indigo-400 group-hover:translate-x-0.5 transition-all" />
              </button>

              <button
                onClick={() => {
                  setActiveTab('USERS');
                  setUserFilter('ALL');
                  setUsersPage(1);
                }}
                className="w-full p-3.5 bg-[#060811] hover:bg-slate-900 border border-slate-800 rounded-2xl text-xs font-bold text-slate-200 flex items-center justify-between transition-all cursor-pointer group"
              >
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-400" /> User Directory & Roles
                </span>
                <ChevronRight className="w-4 h-4 text-slate-500 group-hover:text-purple-400 group-hover:translate-x-0.5 transition-all" />
              </button>
            </div>
          </div>

          <div className="pt-4 mt-6 border-t border-slate-800/80 text-[11px] text-slate-500">
            ScaleSutra Governance Engine v2.0 • Standalone Build
          </div>
        </div>
      </div>
    </div>
  );
};
